import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

// Tables<> merges Tables and Views, so these resolve to the view Row types.
export type ClientDossierOverview = Tables<'client_dossier_overview'>;
export type ClientDossierStage = Tables<'client_dossier_stages'>;
export type ClientDossierFile = Tables<'client_dossier_files'>;

/** Liste des dossiers du client connecté (RLS filtre — aucun param). */
export async function listClientDossiers(): Promise<ClientDossierOverview[]> {
  const { data, error } = await supabase
    .from('client_dossier_overview')
    .select('*')
    .order('opened_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Un dossier précis (0 ligne si hors périmètre). */
export async function getClientDossier(
  dossierId: string,
): Promise<ClientDossierOverview | null> {
  const { data, error } = await supabase
    .from('client_dossier_overview')
    .select('*')
    .eq('id', dossierId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export interface ClientFriseStep {
  position: number;
  label: string;
  effective_on: string | null;
  is_current: boolean;
}

/** Frise copiée en cours. L'ancienne frise n'est pas dans cette liste. */
export async function listClientFrise(dossierId: string): Promise<ClientFriseStep[]> {
  const { data, error } = await (supabase as unknown as {
    from: (table: string) => {
      select: (cols: string) => {
        eq: (col: string, value: string) => {
          order: (col: string) => Promise<{ data: ClientFriseStep[] | null; error: { message: string } | null }>;
        };
      };
    };
  })
    .from('client_dossier_frise')
    .select('position, label, effective_on, is_current')
    .eq('dossier_id', dossierId)
    .order('position');
  if (error) throw error;
  return data ?? [];
}

/** Historique d'étapes d'un dossier, du plus récent au plus ancien. */
export async function listClientDossierStages(
  dossierId: string,
): Promise<ClientDossierStage[]> {
  const { data, error } = await supabase
    .from('client_dossier_stages')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('transitioned_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Documents partagés d'un dossier. */
export async function listClientDossierFiles(
  dossierId: string,
): Promise<ClientDossierFile[]> {
  const { data, error } = await supabase
    .from('client_dossier_files')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export type ClientContact = {
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
};

/** Fiche contact du client connecté — colonnes affichées uniquement. */
export async function getClientContact(contactId: string): Promise<ClientContact | null> {
  const { data, error } = await supabase
    .from('contacts')
    .select('first_name, last_name, company_name, email, phone')
    .eq('id', contactId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Met à jour la préférence d'opt-out notifications du client connecté. */
export async function setClientNotificationsEnabled(
  clientUserId: string,
  enabled: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('client_users')
    .update({ notifications_enabled: enabled })
    .eq('id', clientUserId);
  if (error) throw error;
}

/**
 * URL de téléchargement d'un document partagé.
 * La RPC client_file_storage_path vérifie l'autorisation et renvoie le
 * storage_path ; on signe ensuite l'URL côté client (15 min).
 */
export async function getClientFileDownloadUrl(fileId: string): Promise<string> {
  const { data: path, error } = await supabase.rpc('client_file_storage_path', {
    p_file_id: fileId,
  });
  if (error || !path) throw new Error('Téléchargement impossible');
  const { data, error: signErr } = await supabase.storage
    .from('dossier-files')
    .createSignedUrl(path, 900);
  if (signErr || !data) throw new Error('Téléchargement impossible');
  return data.signedUrl;
}
